import { Badge, Box, Icon, Tabs, Text } from '@chakra-ui/react';
import { HeadingPortal } from '@mono/layout';
import { FC, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FiLayers, FiSettings, FiUsers } from 'react-icons/fi';
import { useCurrentOrganization } from '../organizations/hooks';
import ProjectSettingsTab from '../project-settings/ProjectSettingsTab';
import { useCurrentProject } from './hooks';
import ProjectCollaboratorsTab from '../project-collaborators/ProjectCollaboratorsTab';
import ProjectEnvironmentsTab from '../project-environments/ProjectEnvironmentsTab';

const tabItems = [
  {
    value: 'general',
    label: 'General',
    icon: FiSettings,
    Component: ProjectSettingsTab,
  },
  {
    value: 'collaborators',
    label: 'Collaborators',
    icon: FiUsers,
    Component: ProjectCollaboratorsTab,
  },
  {
    value: 'environments',
    label: 'Environments',
    icon: FiLayers,
    Component: ProjectEnvironmentsTab,
  },
] as const;

const ProjectSettingsPage: FC = () => {
  const params = useParams<{ tab: string; projectId: string }>();
  const navigate = useNavigate();
  const org = useCurrentOrganization();
  const project = useCurrentProject();

  const onChangeTab = (details: { value: string }) => {
    navigate(`/${org?.slug}/p/${params.projectId}/settings/${details.value}`);
  };

  return (
    <Fragment>
      <HeadingPortal>
        {project.data?.project.name} <Badge>Settings</Badge>
      </HeadingPortal>
      <Box
        position={'absolute'}
        top={0}
        left={0}
        width={'100%'}
        height={'100%'}
        overflow={'auto'}
      >
        <Tabs.Root
          value={params.tab ?? 'general'}
          onValueChange={onChangeTab}
          orientation="vertical"
          height={'100%'}
          lazyMount
        >
          <Tabs.List pt={8}>
            {tabItems.map((tab) => (
              <Tabs.Trigger key={tab.value} value={tab.value}>
                <Icon as={tab.icon} />
                <Text fontWeight="semibold">{tab.label}</Text>
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Box flex="1" minW="0" h="full">
            {tabItems.map((tab) => {
              const TabComponent = tab.Component;
              return (
                <Tabs.Content
                  key={tab.value}
                  value={tab.value}
                  h="full"
                  p={8}
                  boxSizing={'border-box'}
                >
                  <TabComponent />
                </Tabs.Content>
              );
            })}
          </Box>
        </Tabs.Root>
      </Box>
    </Fragment>
  );
};

export default ProjectSettingsPage;
